import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';

const upgradeItems = [
  'Flooring', 'Electrical', 'Materials', 'Plumbing', 'Paint', 'Fixtures', 'Other'
];

const DocumentManagement = () => {
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    customer_id: '',
    project_id: '',
    upgrade_item: '',
    current_details: '',
    proposed_details: '',
    notes: ''
  });
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const approvalFormRef = useRef();

  const categories = [
    'Agreements',
    'Blueprints',
    'Invoices',
    'Permits',
    'Contracts',
    'Specifications',
    'Safety Documents',
    'Quality Reports',
    'Financial Documents',
    'Other'
  ];

  const statusOptions = [
    'pending', 'signed', 'approved', 'rejected', 'expired', 'draft'
  ];

  // Form state for new document
  const [newDocument, setNewDocument] = useState({
    title: '',
    description: '',
    category: '',
    project_id: '',
    status: 'Pending',
    customer_visible: true,
    expiry_date: '',
    tags: '',
    file: null
  });

  // Form state for editing document
  const [editDocument, setEditDocument] = useState({});

  useEffect(() => {
    fetchDocuments();
    fetchProjects();
    fetchCustomers();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/documents');
      setDocuments(response.data.data);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/admin/projects');
      setProjects(response.data.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/admin/customers');
      setCustomers(response.data.data);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', newDocument.title);
      formData.append('description', newDocument.description);
      formData.append('category', newDocument.category);
      formData.append('project_id', newDocument.project_id);
      formData.append('status', newDocument.status);
      formData.append('customer_visible', newDocument.customer_visible);
      formData.append('expiry_date', newDocument.expiry_date);
      formData.append('tags', newDocument.tags);
      if (newDocument.file) {
        formData.append('file', newDocument.file);
      }

      const response = await api.post('/admin/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setDocuments(prev => [response.data.data, ...prev]);
      setShowUploadModal(false);
      setNewDocument({
        title: '',
        description: '',
        category: '',
        project_id: '',
        status: 'Pending',
        customer_visible: true,
        expiry_date: '',
        tags: '',
        file: null
      });
    } catch (err) {
      console.error('Failed to upload document:', err);
      alert('Failed to upload document. Please try again.');
    }
  };

  const handleUpdateDocument = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/admin/documents/${editDocument.id}`, editDocument);
      setDocuments(prev => prev.map(d => d.id === editDocument.id ? response.data.data : d));
      setShowEditModal(false);
      setSelectedDocument(null);
    } catch (err) {
      console.error('Failed to update document:', err);
      alert('Failed to update document. Please try again.');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/documents/${documentId}`);
      setDocuments(prev => prev.filter(d => d.id !== documentId));
      if (selectedDocument?.id === documentId) {
        setSelectedDocument(null);
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      alert('Failed to delete document. Please try again.');
    }
  };

  const toggleCustomerVisibility = async (documentId, visible) => {
    try {
      const document = documents.find(d => d.id === documentId);
      const updatedDocument = { ...document, customer_visible: visible };
      const response = await api.put(`/admin/documents/${documentId}`, updatedDocument);
      setDocuments(prev => prev.map(d => d.id === documentId ? response.data.data : d));
    } catch (err) {
      console.error('Failed to update visibility:', err);
      alert('Failed to update visibility. Please try again.');
    }
  };

  const openEditModal = (document) => {
    setEditDocument(document);
    setShowEditModal(true);
  };

  const getStatusLabelAndColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return { label: 'Pending Customer Signature', color: 'bg-yellow-100 text-yellow-800' };
      case 'signed':
        return { label: 'Signed by Customer', color: 'bg-green-100 text-green-800' };
      case 'approved':
        return { label: 'Approved', color: 'bg-blue-100 text-blue-800' };
      case 'rejected':
        return { label: 'Rejected', color: 'bg-red-100 text-red-800' };
      case 'expired':
        return { label: 'Expired', color: 'bg-gray-100 text-gray-800' };
      case 'draft':
        return { label: 'Draft', color: 'bg-gray-100 text-gray-800' };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Agreements': 'bg-purple-100 text-purple-800',
      'Blueprints': 'bg-indigo-100 text-indigo-800',
      'Invoices': 'bg-green-100 text-green-800',
      'Permits': 'bg-orange-100 text-orange-800',
      'Contracts': 'bg-blue-100 text-blue-800',
      'Specifications': 'bg-teal-100 text-teal-800',
      'Safety Documents': 'bg-red-100 text-red-800',
      'Quality Reports': 'bg-emerald-100 text-emerald-800',
      'Financial Documents': 'bg-amber-100 text-amber-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.Other;
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const icons = {
      'pdf': '📄',
      'doc': '📝',
      'docx': '📝',
      'xls': '📊',
      'xlsx': '📊',
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'dwg': '🏗️',
      'rvt': '🏗️'
    };
    return icons[extension] || '📎';
  };

  const filteredDocuments = documents.filter(document => {
    const matchesCategory = filterCategory === 'all' || document.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || document.status === filterStatus;
    const matchesProject = filterProject === 'all' || document.project_id === filterProject;
    const matchesSearch = (document.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (document.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (Array.isArray(document.tags) ? document.tags.join(', ') : document.tags || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesStatus && matchesProject && matchesSearch;
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewDocument({ ...newDocument, file });
  };

  // Filter projects by selected customer
  const filteredProjects = approvalForm.customer_id
    ? projects.filter(p => p.customer === approvalForm.customer_id)
    : projects;

  // Approval Request Handlers
  const openApprovalModal = () => {
    setApprovalForm({
      customer_id: '', project_id: '', upgrade_item: '', current_details: '', proposed_details: '', notes: ''
    });
    setApprovalError('');
    setApprovalModalOpen(true);
  };
  const closeApprovalModal = () => setApprovalModalOpen(false);

  const handleApprovalChange = (e) => {
    const { name, value } = e.target;
    setApprovalForm(f => ({ ...f, [name]: value }));
    if (name === 'customer_id') setApprovalForm(f => ({ ...f, project_id: '' }));
  };

  const handleApprovalSubmit = async (e) => {
    e.preventDefault();
    setApprovalLoading(true);
    setApprovalError('');
    try {
      // Get customer/project names for PDF
      const customer = customers.find(c => c.id === approvalForm.customer_id);
      const project = projects.find(p => p.id === approvalForm.project_id);
      const payload = {
        ...approvalForm,
        customer_name: customer?.full_name || '',
        project_name: project?.name || ''
      };
      const response = await api.post('/admin/approval-request', payload);
      setDocuments(prev => [response.data.data, ...prev]);
      setApprovalModalOpen(false);
    } catch (err) {
      setApprovalError(err.response?.data?.message || 'Failed to create approval request.');
    } finally {
      setApprovalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Documents</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display-bold text-gray-900">Document Management</h1>
          <p className="text-gray-600 mt-1">Upload, organize, and manage all project documents with full control over visibility and access.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openApprovalModal}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-body-semibold text-sm flex items-center space-x-2"
          >
            <span>+ Create Approval Request</span>
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-body-semibold text-sm flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>Upload Document</span>
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Search Documents</label>
            <input
              type="text"
              placeholder="Search by title, description, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Project</label>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterCategory('all');
                setFilterStatus('all');
                setFilterProject('all');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocuments.map(document => (
          <div key={document.id} className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6 hover:shadow-medium transition-shadow duration-200">
            {/* Document Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">{getFileIcon(document.name)}</span>
                  <div className="flex-1">
                    <h3 className="font-display-semibold text-lg text-gray-900 truncate">{document.name}</h3>
                    <p className="text-sm text-gray-500">{document.file_type}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-body-semibold ${getCategoryColor(document.category)}`}>
                    {document.category}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-body-semibold ${getStatusLabelAndColor(document.status).color}`}>
                    {getStatusLabelAndColor(document.status).label}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => openEditModal(document)}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Edit Document"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteDocument(document.id)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete Document"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Document Details */}
            <div className="space-y-3 mb-4">
              <div>
                <p className="text-sm text-gray-600">Project</p>
                <p className="font-medium text-gray-900">{document.project_name || 'General'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Description</p>
                <p className="font-medium text-gray-900 text-sm line-clamp-2">{document.description || 'No description'}</p>
              </div>
              {document.tags && document.tags.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {Array.isArray(document.tags) ? 
                      document.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          {tag.trim()}
                        </span>
                      )) :
                      document.tags.split(',').map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          {tag.trim()}
                        </span>
                      ))
                    }
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Uploaded</p>
                <p className="font-medium text-gray-900 text-sm">
                  {new Date(document.created_at).toLocaleDateString()}
                </p>
              </div>
              {document.expiry_date && (
                <div>
                  <p className="text-sm text-gray-600">Expires</p>
                  <p className={`font-medium text-sm ${
                    new Date(document.expiry_date) < new Date() ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {new Date(document.expiry_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            {/* Visibility Toggle - Only show if customer_visible field exists */}
            {document.hasOwnProperty('customer_visible') && (
              <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={document.customer_visible || false}
                    onChange={(e) => toggleCustomerVisibility(document.id, e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Customer Visible</span>
                </div>
                <div className="text-xs text-gray-500">
                  {document.customer_visible ? '👁️' : '🚫'}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <a
                href={document.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium text-center"
              >
                View
              </a>
              <a
                href={document.file_url}
                download
                className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium text-center"
              >
                Download
              </a>
              <button
                onClick={() => setSelectedDocument(document)}
                className="flex-1 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
              >
                Audit Trail
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📄</div>
          <h3 className="text-lg font-display-semibold text-gray-900 mb-2">No documents found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || filterCategory !== 'all' || filterStatus !== 'all' || filterProject !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by uploading your first document.'
            }
          </p>
          {!searchTerm && filterCategory === 'all' && filterStatus === 'all' && filterProject === 'all' && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Upload First Document
            </button>
          )}
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload New Document</h2>
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
                  <input
                    type="text"
                    value={newDocument.title}
                    onChange={(e) => setNewDocument({...newDocument, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newDocument.category}
                    onChange={(e) => setNewDocument({...newDocument, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project (Optional)</label>
                  <select
                    value={newDocument.project_id}
                    onChange={(e) => setNewDocument({...newDocument, project_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">General Document</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={newDocument.status}
                    onChange={(e) => setNewDocument({...newDocument, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newDocument.description}
                  onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={newDocument.tags}
                    onChange={(e) => setNewDocument({...newDocument, tags: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="important, contract, 2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
                  <input
                    type="date"
                    value={newDocument.expiry_date}
                    onChange={(e) => setNewDocument({...newDocument, expiry_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.dwg,.rvt"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, DWG, RVT
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="documentCustomerVisible"
                  checked={newDocument.customer_visible}
                  onChange={(e) => setNewDocument({...newDocument, customer_visible: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="documentCustomerVisible" className="text-sm text-gray-700">
                  Visible to Customer
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Upload Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Details Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Document Details & Audit Trail</h2>
              <button
                onClick={() => setSelectedDocument(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Document Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Document Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Title</p>
                    <p className="font-medium text-gray-900">{selectedDocument.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Category</p>
                    <p className="font-medium text-gray-900">{selectedDocument.category}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-medium text-gray-900">{selectedDocument.status || 'Pending'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Project</p>
                    <p className="font-medium text-gray-900">{selectedDocument.project_name || 'General'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="font-medium text-gray-900">{selectedDocument.description || 'No description'}</p>
                  </div>
                </div>
              </div>

              {/* Audit Trail */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Audit Trail</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Document Uploaded</p>
                        <p className="text-sm text-gray-600">by {selectedDocument.uploaded_by_name || 'System'}</p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedDocument.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Mock audit trail entries - replace with actual data */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Status Updated</p>
                        <p className="text-sm text-gray-600">Changed to "{selectedDocument.status}" by Admin</p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedDocument.updated_at || selectedDocument.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Viewed by Customer</p>
                        <p className="text-sm text-gray-600">Customer accessed the document</p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(Date.now() - 86400000).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-100">
              <a
                href={selectedDocument.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Document
              </a>
              <button
                onClick={() => setSelectedDocument(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Request Modal */}
      {approvalModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Approval Request</h2>
            <form ref={approvalFormRef} onSubmit={handleApprovalSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                <select
                  name="customer_id"
                  value={approvalForm.customer_id}
                  onChange={handleApprovalChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                <select
                  name="project_id"
                  value={approvalForm.project_id}
                  onChange={handleApprovalChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                  disabled={!approvalForm.customer_id}
                >
                  <option value="">Select Project</option>
                  {filteredProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upgrade Item</label>
                <select
                  name="upgrade_item"
                  value={approvalForm.upgrade_item}
                  onChange={handleApprovalChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="">Select Upgrade Item</option>
                  {upgradeItems.map(item => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Details</label>
                <textarea
                  name="current_details"
                  value={approvalForm.current_details}
                  onChange={handleApprovalChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proposed Details</label>
                <textarea
                  name="proposed_details"
                  value={approvalForm.proposed_details}
                  onChange={handleApprovalChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  name="notes"
                  value={approvalForm.notes}
                  onChange={handleApprovalChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              {approvalError && <div className="text-red-600 text-sm">{approvalError}</div>}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeApprovalModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={approvalLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  disabled={approvalLoading}
                >
                  {approvalLoading ? 'Creating...' : 'Create Approval Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement; 